import { useRef, useState, type ReactNode } from "react";
import { ImageUp, Trash2 } from "lucide-react";
import {
  BUTTON_RADII,
  logoAdvice,
  LOGO_RECOMMENDATION,
  siteDesignAttributes,
  SiteAccentPicker,
  SiteIcon,
  SiteThemePicker,
  type LogoDisplay,
  type LogoSize,
  type SiteDesign,
} from "../../themes";
import { Button, Hint, Notice, SectionHeading } from "../../../components/ui";
import { BottomSheet } from "./BottomSheet";
import { MenuSegmented } from "./Menu";
import "./ButtonBlock.css";

const radiusLabel = (radius: (typeof BUTTON_RADII)[number]) =>
  radius === "0" ? "Square" : radius === "pill" ? "Pill" : `${radius}px`;

/** Where the slider starts for a theme that sets its own corners. */
const themeRadius = (theme: SiteDesign["theme"]) => (theme === "gallery" ? "0" : "pill");

/** The logo in the preview strip, at roughly its header heights. */
const previewHeight: Record<LogoSize, string> = {
  small: "h-6",
  medium: "h-8",
  large: "h-11",
};

type DesignSheetProps = {
  open: boolean;
  inline?: boolean;
  design: SiteDesign;
  siteName: string;
  iconUrl: string | null;
  logoUrl: string | null;
  onSaveDesign: (patch: Partial<SiteDesign>) => Promise<string | null>;
  onUploadIcon: (file: File) => Promise<string | null>;
  onUploadLogo: (file: File) => Promise<string | null>;
  onClose: () => void;
};

/** Site-wide look and feel. */
export const DesignSheet = ({
  open,
  inline = false,
  design,
  siteName,
  iconUrl,
  logoUrl,
  onSaveDesign,
  onUploadIcon,
  onUploadLogo,
  onClose,
}: DesignSheetProps) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  // The roundness slider while it moves; saved once it's let go.
  const [radiusDraft, setRadiusDraft] = useState<SiteDesign["buttonRadius"] | undefined>(undefined);
  const radius = radiusDraft === undefined ? design.buttonRadius : radiusDraft;
  const commitRadius = () => {
    if (radiusDraft === undefined) return;
    if (radiusDraft !== design.buttonRadius) save({ buttonRadius: radiusDraft });
    setRadiusDraft(undefined);
  };

  const run = async (action: () => Promise<string | null>) => {
    // Controls stay enabled while saving so keyboard focus isn't dropped;
    // choices already show optimistically.
    if (saving) return;
    setSaving(true);
    setError((await action()) ?? "");
    setSaving(false);
  };
  // Taps during a save are merged here (last tap wins per setting) and saved
  // once, when the in-flight save finishes.
  const queued = useRef<Partial<SiteDesign> | null>(null);
  const save = (patch: Partial<SiteDesign>) => {
    if (saving) {
      queued.current = { ...queued.current, ...patch };
      return;
    }
    void (async () => {
      let next: Partial<SiteDesign> | null = patch;
      setSaving(true);
      while (next) {
        queued.current = null;
        const failure = await onSaveDesign(next);
        setError(failure ?? "");
        next = failure ? null : queued.current;
      }
      queued.current = null;
      setSaving(false);
    })();
  };

  return (
    <BottomSheet
      inline={inline}
      disabled={saving}
      onClose={onClose}
      open={open}
      title="Design"
      titleId="design-sheet-title"
    >
      <div className="space-y-7">
        <Section title="Theme">
          <SiteThemePicker
            name="site-theme"
            value={design.theme}
            busy={saving}
            onChange={(theme) => theme !== design.theme && save({ theme })}
          />
        </Section>
        <Section title="Accent colour">
          <SiteAccentPicker
            name="site-accent"
            theme={design.theme}
            value={design.accent}
            busy={saving}
            onChange={(accent) => accent !== design.accent && save({ accent })}
          />
          <Hint className="mt-3">Used for links, buttons and highlights.</Hint>
        </Section>
        <Section title="Buttons">
          <div
            {...siteDesignAttributes({ ...design, buttonRadius: radius })}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-hazumu-border bg-site-bg px-4 py-4 text-site-text"
          >
            <span className="site-button" data-style="solid">Get in touch</span>
            <span className="site-button" data-style="outline">See my work</span>
          </div>
          <div className="mt-3 grid gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <label htmlFor="button-roundness" className="text-sm text-hazumu-muted">
                Corners
              </label>
              <span className="text-sm text-hazumu-ink">
                {radius === null ? "Theme’s own" : radiusLabel(radius)}
                {radius !== null && (
                  <button
                    type="button"
                    className="ml-2 text-hazumu-muted underline underline-offset-2 hover:text-hazumu-ink"
                    onClick={() => save({ buttonRadius: null })}
                  >
                    Reset
                  </button>
                )}
              </span>
            </div>
            {/* Square to pill in curated steps; the theme's own until moved. */}
            <input
              id="button-roundness"
              type="range"
              min={0}
              max={BUTTON_RADII.length - 1}
              step={1}
              value={BUTTON_RADII.indexOf(radius ?? themeRadius(design.theme))}
              aria-valuetext={radiusLabel(radius ?? themeRadius(design.theme))}
              className="w-full accent-hazumu-terracotta"
              onChange={(event) => setRadiusDraft(BUTTON_RADII[Number(event.target.value)])}
              onPointerUp={commitRadius}
              onKeyUp={commitRadius}
              onBlur={commitRadius}
            />
          </div>
          <Hint className="mt-3">Every button on your site. Each button’s colour is set on the button itself.</Hint>
        </Section>
        <Section title="Logo">
          <div
            {...siteDesignAttributes(design)}
            className="flex min-h-16 items-center gap-3 rounded-xl border border-hazumu-border bg-site-bg px-4 py-3 text-site-text"
          >
            {logoUrl && (
              <img
                src={logoUrl}
                alt=""
                className={`block w-auto max-w-48 shrink-0 object-contain object-left ${previewHeight[design.logoSize]}`}
              />
            )}
            {(!logoUrl || design.logoDisplay === "both") && (
              <span className="site-heading min-w-0 truncate heading-size-18">{siteName}</span>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              disabled={saving}
              onClick={() => logoInput.current?.click()}
              size="sm"
              variant="secondary"
            >
              <ImageUp size={14} aria-hidden="true" />
              {saving ? "Saving…" : logoUrl ? "Replace" : "Upload"}
            </Button>
            {design.logo && (
              <Button
                disabled={saving}
                onClick={() => save({ logo: null, logoTone: null, logoInfo: null })}
                size="sm"
                variant="secondary"
              >
                <Trash2 size={14} aria-hidden="true" />
                Remove
              </Button>
            )}
          </div>
          <input
            ref={logoInput}
            accept="image/png,image/svg+xml,image/webp,image/jpeg,image/gif"
            aria-label="Upload logo"
            className="sr-only"
            tabIndex={-1}
            type="file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void run(() => onUploadLogo(file));
            }}
          />
          {design.logo && (
            <div className="mt-4 grid gap-2.5">
              <div className="grid gap-1.5">
                <span className="text-sm text-hazumu-muted">Show</span>
                <MenuSegmented<LogoDisplay>
                  fullWidth
                  label="What the header shows"
                  options={[
                    ["logo", "Logo only"],
                    ["both", "Logo and name"],
                  ]}
                  value={design.logoDisplay}
                  onChange={(logoDisplay) => logoDisplay !== design.logoDisplay && save({ logoDisplay })}
                />
              </div>
              <div className="grid gap-1.5">
                <span className="text-sm text-hazumu-muted">Size</span>
                <MenuSegmented<LogoSize>
                  fullWidth
                  label="Logo size"
                  options={[
                    ["small", "Small"],
                    ["medium", "Medium"],
                    ["large", "Large"],
                  ]}
                  value={design.logoSize}
                  onChange={(logoSize) => logoSize !== design.logoSize && save({ logoSize })}
                />
              </div>
            </div>
          )}
          <Hint className="mt-3">{logoAdvice(design) ?? LOGO_RECOMMENDATION}</Hint>
        </Section>
        <Section title="Site icon">
          <div className="flex items-center gap-4">
            <SiteIcon design={design} name={siteName} iconUrl={iconUrl} />
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={saving}
                onClick={() => fileInput.current?.click()}
                size="sm"
                variant="secondary"
              >
                <ImageUp size={14} aria-hidden="true" />
                {saving ? "Saving…" : iconUrl ? "Replace" : "Upload"}
              </Button>
              {design.icon && (
                <Button
                  disabled={saving}
                  onClick={() => save({ icon: null })}
                  size="sm"
                  variant="secondary"
                >
                  <Trash2 size={14} aria-hidden="true" />
                  Remove
                </Button>
              )}
            </div>
            <input
              ref={fileInput}
              accept="image/png,image/jpeg,image/webp,image/gif"
              aria-label="Upload site icon"
              className="sr-only"
              tabIndex={-1}
              type="file"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void run(() => onUploadIcon(file));
              }}
            />
          </div>
          <Hint className="mt-3">
            Shown in browser tabs and bookmarks. Square images work best;
            without one, your site’s first letter is used.
          </Hint>
        </Section>
        <Hint>Publish to update your live site.</Hint>
        {error && <Notice>{error}</Notice>}
      </div>
    </BottomSheet>
  );
};

// Relative, so its hidden file inputs stay inside the scrolling panel
// rather than stretching the page.
const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="relative">
    <SectionHeading>{title}</SectionHeading>
    {children}
  </section>
);
